/**
 * business-intelligence — «Бизнес-аналитика»: упоминания конкурентов, отзывы
 * о продукте, рыночные сигналы, флаги комплаенса. Секция новая — в старом
 * printReport.ts её не было вовсе. Зеркалит BusinessIntelligenceSection.tsx:
 * тот же гейт (суммарно ноль элементов по всем четырём группам → секции
 * нет), тот же порядок групп (конкуренты → отзывы → сигналы → флаги),
 * внутри группы — тот же порядок, что в исходном массиве.
 *
 * Раскладка группы — `.blk` (заголовок `.lbl` + строки `.chk`), тот же
 * примитив (`block()`), что уже несёт блоки «Диалог»/«Возражения» и строки
 * комплаенса; здесь `.chk` используется не для иконки-вердикта, а как
 * обычная flex-строка «бейдж + текст» — класс не создаёт новую семантику,
 * только даёт разметку.
 *
 * Цвет бейджа:
 *  - конкуренты — `sentiment` (positive/neutral/negative → ok/muted/bad),
 *    как `SENTIMENT_VARIANT` в BusinessIntelligenceSection.tsx;
 *  - отзывы — `severity` (praise → ok, minor_issue → warn,
 *    major_issue/incident → bad), как `SEVERITY_VARIANT` там же;
 *  - сигналы — всегда muted: экран красит все `marketSignals` одним и тем же
 *    нейтральным вариантом независимо от `type`;
 *  - флаги — всегда bad (сам текст флага — бейдж, `context` — цитата
 *    рядом), как `Tag variant="danger"` в блоке complianceFlags на экране.
 *
 * `sentiment`/`severity`/`type` печатаются СЫРОЙ строкой без перевода — как
 * `priority` в recommendations.ts (экран тоже их не переводит:
 * `{m.sentiment}`/`{fb.severity}`/`{sig.type}` — литерал прямо в JSX).
 * Экранирование этих полей — через `tag()` (примитив уже экранирует текст
 * бейджа), отдельного вызова `escapeHtml` для них не нужно.
 *
 * Контекст/цитата — через «…», как в остальном отчёте (см. key-moments.ts),
 * а не экранные кавычки `&ldquo;…&rdquo;` (BusinessIntelligenceSection.tsx).
 */
import { card, tag, block } from '../primitives';
import { escapeHtml } from '../escape';
import type { SectionFn } from '../types';

const SENTIMENT_VARIANT: Record<string, 'ok' | 'muted' | 'bad'> = {
  positive: 'ok',
  neutral: 'muted',
  negative: 'bad',
};

const SEVERITY_VARIANT: Record<string, 'ok' | 'warn' | 'bad'> = {
  praise: 'ok',
  minor_issue: 'warn',
  major_issue: 'bad',
  incident: 'bad',
};

export const renderBusinessIntelligence: SectionFn = (input, t) => {
  const bi = input.analysis.result.businessIntelligence;
  const competitorMentions = bi?.competitorMentions ?? [];
  const productFeedback = bi?.productFeedback ?? [];
  const marketSignals = bi?.marketSignals ?? [];
  const complianceFlags = bi?.complianceFlags ?? [];

  const total =
    competitorMentions.length +
    productFeedback.length +
    marketSignals.length +
    complianceFlags.length;
  if (total === 0) return '';

  const competitorHtml =
    competitorMentions.length > 0
      ? block(
          t('records.analysisPanel.competitorMentions', 'Competitor mentions'),
          competitorMentions
            .map(
              (m) =>
                `<div class="chk">${tag(m.sentiment, SENTIMENT_VARIANT[m.sentiment] ?? 'muted')}` +
                `<span><b>${escapeHtml(m.name)}</b> — «${escapeHtml(m.context)}»</span></div>`
            )
            .join('')
        )
      : '';

  const feedbackHtml =
    productFeedback.length > 0
      ? block(
          t('records.analysisPanel.productFeedback', 'Product feedback'),
          productFeedback
            .map(
              (fb) =>
                `<div class="chk">${tag(fb.severity, SEVERITY_VARIANT[fb.severity] ?? 'muted')}` +
                `<span><b>${escapeHtml(fb.product)}</b> — ${escapeHtml(fb.feedback)}</span></div>`
            )
            .join('')
        )
      : '';

  const signalsHtml =
    marketSignals.length > 0
      ? block(
          t('records.analysisPanel.marketSignals', 'Market signals'),
          marketSignals
            .map(
              (sig) =>
                `<div class="chk">${tag(sig.type, 'muted')}<span>${escapeHtml(sig.signal)}</span></div>`
            )
            .join('')
        )
      : '';

  const flagsHtml =
    complianceFlags.length > 0
      ? block(
          t('records.analysisPanel.complianceFlags', 'Compliance flags'),
          complianceFlags
            .map(
              (flag) =>
                `<div class="chk">${tag(flag.flag, 'bad')}<span>«${escapeHtml(flag.context)}»</span></div>`
            )
            .join('')
        )
      : '';

  return card({
    title: t('records.analysisPanel.businessIntelligence', 'Business Intelligence'),
    value: `<span style="color:var(--fg3)">${total}</span>`,
    body: competitorHtml + feedbackHtml + signalsHtml + flagsHtml,
  });
};
